import React, {useEffect, useMemo, useState} from "react";
import {useSelector} from "react-redux";
import {css} from "@panda/css";

import {Loader} from "@pg-design/loader-module";
import {IOption, Select} from "@pg-design/select-module";
import {Tabs} from "@pg-design/tabs";
import {Text} from "@pg-design/text-module";
import {useIsMounted} from "@pg-mono/hooks";
import {uniqBy} from "@pg-mono/nodash";
import {RequestState} from "@pg-mono/request-state";
import {RegionType} from "@pg-rp/common-types";

import {IRPStore} from "../../../app/rp_reducer";
import {getAdjustedSliderSettings} from "../../../app/utils/get_adjusted_slider_settings";
import {EmptyList} from "../../../offer/atoms/EmptyList";
import {OfferBoxWithOfferDetailsAction} from "../../../offer/atoms/OfferBoxWithOfferDetailsAction";
import {IOfferBox} from "../../../offer/components/offer_box/OfferBox";
import {HomepageOfferListingModal} from "../../../offer/list/components/HomepageOfferListingModal";
import {DEFAULT_DISTANCE_FOR_TOWN} from "../../../offer/list/constants/offer_list";
import {getOfferCardAddress, IOfferCardAddressContext} from "../../../offer/utils/get_offer_card_address";
import {useGTMEcommerceOfferListViewTracking} from "../../../tracking/hooks/use_gtm_ecommerce_offer_list_view_tracking";
import {regionDefaultOption} from "../../constants/offer_list";
import {slickSliderSettings} from "../../constants/slick_slider_settings";
import {useHomepageRegionOptions} from "../../hooks/use_homepage_region_options";
import {MoreOffersBox} from "./MoreOffersBox";
import {SlickWrapper} from "./SlickWrapper";

export const LazySlider = React.lazy(() => import("@pg-mono/slick-slider"));

const offerAddressContext: IOfferCardAddressContext = {
    searchedRegion: {type: RegionType.CITY, fromOfferStats: true},
    searchDistance: DEFAULT_DISTANCE_FOR_TOWN
};

export interface IRegionOption extends IOption {
    slug: string;
}

interface IProps {
    moreOffersText?: string;
    getMoreOffersLink?: (regionValue: IRegionOption, sortValue: string) => string;
    onChange: (regionValue: IRegionOption, sortValue: string) => void;
    sortOptions: {title: React.ReactNode; value: string}[];
    sortInitialValue: string;
    listId: string;
    listTitle: string;
    gtmTileEvents: IOfferBox["trackingMeta"]["tileEvents"];
}

export const OffersPanel = (props: IProps) => {
    const isMounted = useIsMounted();

    const [regionValue, setRegionValue] = useState<IRegionOption>(regionDefaultOption);
    const [sortValue, setOfferFilterTypeValue] = useState(props.sortInitialValue);
    const [activeSlide, setActiveSlide] = useState(0);

    // return recomended offer list without repetition
    const recommendedOfferList = uniqBy(
        useSelector((s: IRPStore) => s.homepage.recommendedOfferList),
        (item) => item.id
    );
    const recommendedOfferListRequest = useSelector((state: IRPStore) => state.homepage.requestState);

    const homepageOptions = useHomepageRegionOptions();

    const {containerId} = useGTMEcommerceOfferListViewTracking({
        offers: recommendedOfferList,
        listId: props.listId,
        listName: props.listTitle,
        useVisibilityTracking: true,
        isListReady: recommendedOfferListRequest === RequestState.Success
    });

    useEffect(() => {
        if (homepageOptions) {
            const currentTranslatedOption = homepageOptions.regionOptions.find((option) => option.value === regionValue.value);

            setRegionValue(currentTranslatedOption ? currentTranslatedOption : regionValue);
        }
    }, [homepageOptions?.regionDefaultOption.label]);

    const onRegionValueChange = (value: IRegionOption) => {
        setRegionValue(value);

        props.onChange(value, sortValue);
    };

    const onOfferTypeValueChange = (value: string) => {
        setOfferFilterTypeValue(value);

        props.onChange(regionValue, value);
    };

    const loader = (
        <div className={loaderWrapper}>
            <Loader size="lg" />
        </div>
    );

    const showLoader = recommendedOfferListRequest === RequestState.None || recommendedOfferListRequest === RequestState.Waiting;

    const showMoreOffersLink = props.getMoreOffersLink?.(regionValue, sortValue) || "";
    const showMoreOffers = !!props.moreOffersText && !!props.getMoreOffersLink;

    const itemCount = recommendedOfferList.length + Number(showMoreOffers);
    const adjustedSliderSettings = useMemo(() => getAdjustedSliderSettings(slickSliderSettings, itemCount), [itemCount]);

    return (
        <div id={containerId} data-id={containerId}>
            <div className={header}>
                <Text variant="headline_2" strong>
                    {props.listTitle}
                </Text>

                <div className={filters}>
                    <div className={tabsWrapper}>
                        <Tabs
                            activeColor="gray"
                            fullWidth
                            onChange={onOfferTypeValueChange}
                            color="gray"
                            tabs={props.sortOptions}
                            value={sortValue}
                            variant="contained"
                        />
                    </div>

                    <div className={regions} id="offers-panel-region">
                        {homepageOptions && (
                            <Select
                                instanceId="region-select"
                                options={homepageOptions.regionOptions}
                                value={regionValue}
                                onChange={(value) => onRegionValueChange(value as IRegionOption)}
                            />
                        )}
                    </div>
                </div>
            </div>

            <HomepageOfferListingModal />

            {!isMounted && showLoader && loader}

            {isMounted && (
                <React.Suspense fallback={loader}>
                    {showLoader ? (
                        loader
                    ) : (
                        <>
                            {recommendedOfferList.length === 0 ? (
                                <div className={emptyListWrap}>
                                    <EmptyList />
                                </div>
                            ) : (
                                <SlickWrapper
                                    isOnFirstSlide={activeSlide === 0}
                                    isOnLastSlide={activeSlide === recommendedOfferList.length + Number(showMoreOffers) - 1}
                                >
                                    <LazySlider {...adjustedSliderSettings} beforeChange={(prev: number, next: number) => setActiveSlide(next)}>
                                        {recommendedOfferList.map((offer, index) => {
                                            return (
                                                <div key={offer.id} className={offerBoxWrapper}>
                                                    <OfferBoxWithOfferDetailsAction
                                                        offer={offer}
                                                        displayAddress={getOfferCardAddress(offer, offerAddressContext)}
                                                        index={index}
                                                        trackingMeta={{
                                                            listId: props.listId,
                                                            listName: props.listTitle,
                                                            source: "additional",
                                                            tileEvents: props.gtmTileEvents
                                                        }}
                                                    />
                                                </div>
                                            );
                                        })}

                                        {showMoreOffers && (
                                            <div className={offerBoxWrapper}>
                                                <MoreOffersBox link={showMoreOffersLink} text={props.moreOffersText as string} />
                                            </div>
                                        )}
                                    </LazySlider>
                                </SlickWrapper>
                            )}
                        </>
                    )}
                </React.Suspense>
            )}
        </div>
    );
};

const loaderWrapper = css({
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    width: "100%",
    flexBasis: "100%",
    height: "50rem"
});

const header = css({
    mb: 2,
    px: 2,
    sm: {
        px: 0,
        mb: 0
    },
    md: {
        mb: 0
    }
});

const filters = css({
    display: "flex",
    flexDirection: "column",
    mt: 3,
    mb: 2,
    position: "relative",

    md: {
        flexDirection: "row",
        mt: 4
    }
});

const regions = css({
    md: {
        width: "16.8rem",
        minWidth: "16.8rem",
        marginLeft: 3
    },
    xl: {
        minWidth: "36rem"
    }
});

const emptyListWrap = css({
    py: 4,
    px: 2,

    sm: {
        px: 0
    }
});

const offerBoxWrapper = css({
    maxWidth: "302px",
    height: "100%",

    xs: {
        minWidth: "360px",
        maxWidth: "384px",
        pr: 3
    }
});

const tabsWrapper = css({
    overflowY: "auto",
    mb: 3,
    width: "100%",

    "&::before, &::after ": {
        content: "",
        position: "absolute",
        top: 0,
        width: "8rem",
        height: "100%"
    },

    md: {
        mb: 0,

        "&::after ": {
            display: "none"
        }
    }
});
import React, {useEffect, useMemo, useState} from "react";
import {useSelector} from "react-redux";
import {css} from "@panda/css";

import {Loader} from "@pg-design/loader-module";
import {IOption, Select} from "@pg-design/select-module";
import {Tabs} from "@pg-design/tabs";
import {Text} from "@pg-design/text-module";
import {useIsMounted} from "@pg-mono/hooks";
import {uniqBy} from "@pg-mono/nodash";
import {RequestState} from "@pg-mono/request-state";
import {RegionType} from "@pg-rp/common-types";

import {IRPStore} from "../../../app/rp_reducer";
import {getAdjustedSliderSettings} from "../../../app/utils/get_adjusted_slider_settings";
import {EmptyList} from "../../../offer/atoms/EmptyList";
import {OfferBoxWithOfferDetailsAction} from "../../../offer/atoms/OfferBoxWithOfferDetailsAction";
import {IOfferBox} from "../../../offer/components/offer_box/OfferBox";
import {HomepageOfferListingModal} from "../../../offer/list/components/HomepageOfferListingModal";
import {DEFAULT_DISTANCE_FOR_TOWN} from "../../../offer/list/constants/offer_list";
import {getOfferCardAddress, IOfferCardAddressContext} from "../../../offer/utils/get_offer_card_address";
import {useGTMEcommerceOfferListViewTracking} from "../../../tracking/hooks/use_gtm_ecommerce_offer_list_view_tracking";
import {regionDefaultOption} from "../../constants/offer_list";
import {slickSliderSettings} from "../../constants/slick_slider_settings";
import {useHomepageRegionOptions} from "../../hooks/use_homepage_region_options";
import {MoreOffersBox} from "./MoreOffersBox";
import {SlickWrapper} from "./SlickWrapper";

export const LazySlider = React.lazy(() => import("@pg-mono/slick-slider"));

const offerAddressContext: IOfferCardAddressContext = {
    searchedRegion: {type: RegionType.CITY, fromOfferStats: true},
    searchDistance: DEFAULT_DISTANCE_FOR_TOWN
};

export interface IRegionOption extends IOption {
    slug: string;
}

interface IProps {
    moreOffersText?: string;
    getMoreOffersLink?: (regionValue: IRegionOption, sortValue: string) => string;
    onChange: (regionValue: IRegionOption, sortValue: string) => void;
    sortOptions: {title: React.ReactNode; value: string}[];
    sortInitialValue: string;
    listId: string;
    listTitle: string;
    gtmTileEvents: IOfferBox["trackingMeta"]["tileEvents"];
}

export const OffersPanel = (props: IProps) => {
    const isMounted = useIsMounted();

    const [regionValue, setRegionValue] = useState<IRegionOption>(regionDefaultOption);
    const [sortValue, setOfferFilterTypeValue] = useState(props.sortInitialValue);
    const [activeSlide, setActiveSlide] = useState(0);

    // return recomended offer list without repetition
    const recommendedOfferList = uniqBy(
        useSelector((s: IRPStore) => s.homepage.recommendedOfferList),
        (item) => item.id
    );
    const recommendedOfferListRequest = useSelector((state: IRPStore) => state.homepage.requestState);

    const homepageOptions = useHomepageRegionOptions();

    const {containerId} = useGTMEcommerceOfferListViewTracking({
        offers: recommendedOfferList,
        listId: props.listId,
        listName: props.listTitle,
        useVisibilityTracking: true,
        isListReady: recommendedOfferListRequest === RequestState.Success
    });

    useEffect(() => {
        if (homepageOptions) {
            const currentTranslatedOption = homepageOptions.regionOptions.find((option) => option.value === regionValue.value);

            setRegionValue(currentTranslatedOption ? currentTranslatedOption : regionValue);
        }
    }, [homepageOptions?.regionDefaultOption.label]);

    const onRegionValueChange = (value: IRegionOption) => {
        setRegionValue(value);

        props.onChange(value, sortValue);
    };

    const onOfferTypeValueChange = (value: string) => {
        setOfferFilterTypeValue(value);

        props.onChange(regionValue, value);
    };

    const loader = (
        <div className={loaderWrapper}>
            <Loader size="lg" />
        </div>
    );

    const showLoader = recommendedOfferListRequest === RequestState.None || recommendedOfferListRequest === RequestState.Waiting;

    const showMoreOffersLink = props.getMoreOffersLink?.(regionValue, sortValue) || "";
    const showMoreOffers = !!props.moreOffersText && !!props.getMoreOffersLink;

    const itemCount = recommendedOfferList.length + Number(showMoreOffers);
    const adjustedSliderSettings = useMemo(() => getAdjustedSliderSettings(slickSliderSettings, itemCount), [itemCount]);

    return (
        <div id={containerId} data-id={containerId}>
            <div className={header}>
                <Text variant="headline_2" strong>
                    {props.listTitle}
                </Text>

                <div className={filters}>
                    <div className={tabsWrapper}>
                        <Tabs
                            activeColor="gray"
                            fullWidth
                            onChange={onOfferTypeValueChange}
                            color="gray"
                            tabs={props.sortOptions}
                            value={sortValue}
                            variant="contained"
                        />
                    </div>

                    <div className={regions} id="offers-panel-region">
                        {homepageOptions && (
                            <Select
                                instanceId="region-select"
                                options={homepageOptions.regionOptions}
                                value={regionValue}
                                onChange={(value) => onRegionValueChange(value as IRegionOption)}
                            />
                        )}
                    </div>
                </div>
            </div>

            <HomepageOfferListingModal />

            {!isMounted && showLoader && loader}

            {isMounted && (
                <React.Suspense fallback={loader}>
                    {showLoader ? (
                        loader
                    ) : (
                        <>
                            {recommendedOfferList.length === 0 ? (
                                <div className={emptyListWrap}>
                                    <EmptyList />
                                </div>
                            ) : (
                                <SlickWrapper
                                    isOnFirstSlide={activeSlide === 0}
                                    isOnLastSlide={activeSlide === recommendedOfferList.length + Number(showMoreOffers) - 1}
                                >
                                    <LazySlider {...adjustedSliderSettings} beforeChange={(prev: number, next: number) => setActiveSlide(next)}>
                                        {recommendedOfferList.map((offer, index) => {
                                            return (
                                                <div key={offer.id} className={offerBoxWrapper}>
                                                    <OfferBoxWithOfferDetailsAction
                                                        offer={offer}
                                                        displayAddress={getOfferCardAddress(offer, offerAddressContext)}
                                                        index={index}
                                                        trackingMeta={{
                                                            listId: props.listId,
                                                            listName: props.listTitle,
                                                            source: "additional",
                                                            tileEvents: props.gtmTileEvents
                                                        }}
                                                    />
                                                </div>
                                            );
                                        })}

                                        {showMoreOffers && (
                                            <div className={offerBoxWrapper}>
                                                <MoreOffersBox link={showMoreOffersLink} text={props.moreOffersText as string} />
                                            </div>
                                        )}
                                    </LazySlider>
                                </SlickWrapper>
                            )}
                        </>
                    )}
                </React.Suspense>
            )}
        </div>
    );
};

const loaderWrapper = css({
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    width: "100%",
    flexBasis: "100%",
    height: "50rem"
});

const header = css({
    mb: 2,
    px: 2,
    sm: {
        px: 0,
        mb: 0
    },
    md: {
        mb: 0
    }
});

const filters = css({
    display: "flex",
    flexDirection: "column",
    mt: 3,
    mb: 2,
    position: "relative",

    md: {
        flexDirection: "row",
        mt: 4
    }
});

const regions = css({
    md: {
        width: "16.8rem",
        minWidth: "16.8rem",
        marginLeft: 3
    },
    xl: {
        minWidth: "36rem"
    }
});

const emptyListWrap = css({
    py: 4,
    px: 2,

    sm: {
        px: 0
    }
});

const offerBoxWrapper = css({
    maxWidth: "302px",
    height: "100%",

    xs: {
        minWidth: "360px",
        maxWidth: "384px",
        pr: 3
    }
});

const tabsWrapper = css({
    overflowY: "auto",
    mb: 3,
    width: "100%",

    "&::before, &::after ": {
        content: "",
        position: "absolute",
        top: 0,
        width: "8rem",
        height: "100%"
    },

    md: {
        mb: 0,

        "&::after ": {
            display: "none"
        }
    }
});
